import React, { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, UserCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// HR Data Hover Card Component (named export)
export const HRDataHoverCard = ({ user }) => {
  const [hrData, setHrData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHrData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/hr/data/${user.id}`);
      setHrData(response.data);
    } catch (error) {
      setHrData(null);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    // Trigger the parent's openHrDataDialog function
    window.dispatchEvent(new CustomEvent('openHrData', { detail: user }));
  };

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          onClick={openDialog}
          onMouseEnter={fetchHrData}
          data-testid={`hr-data-${user.id}`}
          title="HR Data (HR Team Only)"
          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
        >
          <UserCircle className="h-4 w-4" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" side="left">
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-teal-900">{user.full_name}</h4>
          {loading ? (
            <div className="flex items-center gap-2 py-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span className="text-xs text-slate-500">Loading...</span>
            </div>
          ) : hrData ? (
            <div className="space-y-1 text-xs">
              {hrData.date_of_joining && (
                <p><strong>DOJ:</strong> {hrData.date_of_joining}</p>
              )}
              {hrData.current_annual_ctc && (
                <p><strong>Current CTC:</strong> {(hrData.currency || 'INR') === 'INR' ? '\u20B9' : '$'}{hrData.current_annual_ctc.toLocaleString()}</p>
              )}
              {hrData.work_location && (
                <p><strong>Location:</strong> {hrData.work_location}</p>
              )}
              {hrData.date_of_birth && (
                <p><strong>DOB:</strong> {hrData.date_of_birth}</p>
              )}
              {hrData.marital_status && (
                <p><strong>Marital:</strong> {hrData.marital_status}</p>
              )}
              {hrData.hometown && (
                <p><strong>Hometown:</strong> {hrData.hometown}</p>
              )}
              {!hrData.date_of_joining && !hrData.current_annual_ctc && !hrData.work_location && (
                <p className="text-slate-400 italic">No HR data available</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400">Click to add HR data</p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const HrDataDialog = React.memo(function HrDataDialog({
  open, onOpenChange, hrDataUser, hrData, setHrData, loadingHrData,
  savingHrData, onSave
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCircle className="h-5 w-5 text-teal-600" />
            HR Data - {hrDataUser?.full_name}
          </DialogTitle>
          <DialogDescription>
            Personal and employment information (HR team only)
          </DialogDescription>
        </DialogHeader>

        {loadingHrData ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Employment Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_of_joining">Date of Joining</Label>
                  <Input
                    id="date_of_joining"
                    type="date"
                    value={hrData.date_of_joining || ''}
                    onChange={(e) => setHrData({...hrData, date_of_joining: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="work_location">OnSite or Remote</Label>
                  <Select value={hrData.work_location || ''} onValueChange={(val) => setHrData({...hrData, work_location: val})}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OnSite">OnSite</SelectItem>
                      <SelectItem value="Remote">Remote</SelectItem>
                      <SelectItem value="Hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Compensation</h3>

              {/* Currency Selection */}
              <div className="space-y-2">
                <Label>Currency</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="currency"
                      value="INR"
                      checked={(hrData.currency || 'INR') === 'INR'}
                      onChange={(e) => setHrData({...hrData, currency: e.target.value})}
                      className="h-4 w-4 text-teal-600"
                    />
                    <span className="text-sm">INR (&#8377;)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="currency"
                      value="USD"
                      checked={hrData.currency === 'USD'}
                      onChange={(e) => setHrData({...hrData, currency: e.target.value})}
                      className="h-4 w-4 text-teal-600"
                    />
                    <span className="text-sm">USD ($)</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_annual_ctc">Start Annual CTC</Label>
                  <Input
                    id="start_annual_ctc"
                    type="text"
                    value={hrData.start_annual_ctc || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d]/g, '');
                      const maxVal = (hrData.currency || 'INR') === 'INR' ? 9999999 : 9999999;
                      if (val === '' || (parseInt(val) <= maxVal)) {
                        setHrData({...hrData, start_annual_ctc: val ? parseInt(val) : null});
                      }
                    }}
                    placeholder={(hrData.currency || 'INR') === 'INR' ? '\u20B9 99,99,999' : '$ 9,999,999'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="current_annual_ctc">Current Annual CTC</Label>
                  <Input
                    id="current_annual_ctc"
                    type="text"
                    value={hrData.current_annual_ctc || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d]/g, '');
                      const maxVal = (hrData.currency || 'INR') === 'INR' ? 9999999 : 9999999;
                      if (val === '' || (parseInt(val) <= maxVal)) {
                        setHrData({...hrData, current_annual_ctc: val ? parseInt(val) : null});
                      }
                    }}
                    placeholder={(hrData.currency || 'INR') === 'INR' ? '\u20B9 99,99,999' : '$ 9,999,999'}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_appraisal_date">Last Appraisal Date</Label>
                  <Input
                    id="last_appraisal_date"
                    type="date"
                    value={hrData.last_appraisal_date || ''}
                    onChange={(e) => setHrData({...hrData, last_appraisal_date: e.target.value})}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    value={hrData.date_of_birth || ''}
                    onChange={(e) => setHrData({...hrData, date_of_birth: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="marital_status">Marital Status</Label>
                  <Select value={hrData.marital_status || ''} onValueChange={(val) => setHrData({...hrData, marital_status: val})}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date_of_anniversary">Date of Anniversary</Label>
                  <Input
                    id="date_of_anniversary"
                    type="date"
                    value={hrData.date_of_anniversary || ''}
                    onChange={(e) => setHrData({...hrData, date_of_anniversary: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hometown">HomeTown (where parents live)</Label>
                  <Input
                    id="hometown"
                    type="text"
                    value={hrData.hometown || ''}
                    onChange={(e) => setHrData({...hrData, hometown: e.target.value})}
                    placeholder="City/Town"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hobbies">Hobbies</Label>
                <Textarea
                  id="hobbies"
                  value={hrData.hobbies || ''}
                  onChange={(e) => setHrData({...hrData, hobbies: e.target.value})}
                  placeholder="List hobbies and interests..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={savingHrData || loadingHrData} className="bg-teal-600 hover:bg-teal-700">
            {savingHrData ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</> : 'Save HR Data'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default HrDataDialog;
